import type { ReactElement } from 'react';
import { formatDate } from '@xdocs/i18n';
import type { AdminAssetRow, ModerationStatus } from '@xdocs/schemas';
import { Badge, Button, Modal, cn, useI18n } from '@xdocs/ui';
import type { BadgeTone } from '@xdocs/ui';
import { AssetPreviewThumb } from './AssetPreviewThumb.js';
import { useAssetsT } from './useAssetsT.js';
import styles from './AssetDetailPanel.module.css';

const MODERATION_TONE: Record<ModerationStatus, BadgeTone> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
};

export interface AssetDetailPanelProps {
  asset: AdminAssetRow;
  onClose: () => void;
  onUnpublish: () => void;
  onRemove: () => void;
}

/** F60 inspect view — preview + metadata + owner, then Unpublish/Remove. */
export function AssetDetailPanel({ asset, onClose, onUnpublish, onRemove }: AssetDetailPanelProps): ReactElement {
  const { locale } = useI18n();
  const { t } = useAssetsT();

  return (
    <Modal open onClose={onClose} title={t('adminAssets:detail.title')} size="sm">
      <div className={cn(styles['detail'])}>
        <div className={cn(styles['previewRow'])}>
          <AssetPreviewThumb preview={asset.preview} alt="" />
          <span className={cn(styles['assetTitle'])}>{asset.title}</span>
        </div>

        <dl className={cn(styles['fields'])}>
          <div>
            <dt>{t('adminAssets:detail.field.type')}</dt>
            <dd>{t(`adminAssets:type.${asset.type}`)}</dd>
          </div>
          <div>
            <dt>{t('adminAssets:detail.field.owner')}</dt>
            <dd className={cn(styles['mono'])}>{asset.ownerDisplayName ?? asset.ownerId}</dd>
          </div>
          <div>
            <dt>{t('adminAssets:detail.field.moderation')}</dt>
            <dd>
              {asset.moderationStatus ? (
                <Badge tone={MODERATION_TONE[asset.moderationStatus]}>{t(`adminAssets:moderation.${asset.moderationStatus}`)}</Badge>
              ) : (
                t('adminAssets:moderation.none')
              )}
            </dd>
          </div>
          <div>
            <dt>{t('adminAssets:detail.field.created')}</dt>
            <dd>{formatDate(locale, asset.createdAt, { dateStyle: 'medium', timeStyle: 'short' })}</dd>
          </div>
        </dl>

        <div className={cn(styles['actions'])}>
          {asset.type !== 'file' && (
            <Button variant="secondary" onClick={onUnpublish}>
              {t('adminAssets:detail.action.unpublish')}
            </Button>
          )}
          <Button variant="destructive" onClick={onRemove}>
            {t('adminAssets:detail.action.remove')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
